import { useSelector } from 'react-redux'
import postcss from 'postcss'
import postcssContrastColorFunction from '@csstools/postcss-contrast-color-function'

/**
 * Given an Immutable.Map, this function creates a flat object from it,
 * preserving the nesting structure of the individual keys as the key of the
 * resulting flat object.
 *
 * See ./__tests__/ThemeSettingsVariables.spec.jsx for an example
 *
 * @private
 * @param {Immutable.Map} map
 * @returns {object} The flat object
 */
export function createFlatObjectWithPath(map, path = '', delimiter = '-') {
  return map.reduce((acc, val, key) => {
    const computedPath = (path ? `${path}${delimiter}` : '') + key

    return typeof val !== 'object'
      ? { ...acc, [computedPath]: val }
      : { ...acc, ...createFlatObjectWithPath(val, computedPath, delimiter) }
  }, {})
}

export default function ThemeStylesForEditorPreview() {
  const themeCss = useSelector<State, string>((state) => state.get('themeVariablesCss'))
  const themeSettings = useSelector<State, ImmutableMap>((state) => state.getIn(['shop', 'themeSettings']))
  const defaultThemeSettings = useSelector<State, ImmutableMap>((state) => state.get('defaultThemeSettings'))

  // happens during clientside theme switch
  if (!themeSettings) return null

  const variables = createFlatObjectWithPath(defaultThemeSettings.merge(themeSettings))

  const cssTextWithThemeSettings = Object.keys(variables).reduce(
    (css, variable) => css.replace(new RegExp(`var\\(--${variable}\\)`, 'g'), variables[variable]),
    themeCss,
  )

  const { css } = postcss([postcssContrastColorFunction({ preserve: false })]).process(cssTextWithThemeSettings, {
    from: undefined,
  })

  return <style dangerouslySetInnerHTML={{ __html: css }} />
}
